import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

/**
 * Axios client para Nexus.
 *
 * Decisiones:
 * - Base URL configurable por env (`VITE_API_BASE`). Default: api-nexus.mixcore.club.
 * - Inyecta JWT de `localStorage.nx_token` en cada request a menos que el
 *   caller pase explícitamente un `Authorization` distinto.
 * - 401 → limpia token + redirige a /login.
 * - 403 → emite evento custom para que la app muestre un toast genérico
 *   ("Te falta el permiso X para hacer esto") sin que cada llamada tenga
 *   que manejarlo.
 */

const API_BASE = import.meta.env.VITE_API_BASE || "https://api-nexus.mixcore.club";

const TOKEN_KEY = "nx_token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage no disponible */
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const explicit = config.headers?.Authorization;
  if (!explicit) {
    const t = getToken();
    if (t) {
      config.headers = config.headers ?? ({} as InternalAxiosRequestConfig["headers"]);
      (config.headers as Record<string, string>).Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    const status = err.response?.status;

    if (status === 401) {
      setToken(null);
      window.dispatchEvent(new CustomEvent("nx:auth:expired"));
    }

    if (status === 403) {
      const data = err.response?.data as { message?: string; missing_permission?: string } | undefined;
      window.dispatchEvent(
        new CustomEvent("nx:auth:forbidden", {
          detail: {
            message: data?.message || "No tienes permisos para esta acción.",
            missingPermission: data?.missing_permission,
          },
        }),
      );
    }

    return Promise.reject(err);
  },
);
